/**
 * Recently viewed products.
 *
 * Client-rendered from localStorage on purpose: baking history into HTML
 * would let Shopify's full-page cache serve one shopper's trail to the
 * next (same reasoning as the vehicle bar). The current product registers
 * itself only after the previous entries have painted, so the rail shows
 * where you came from, not where you are.
 *
 * Stored entries come from localStorage and are written into the DOM with
 * textContent / setAttribute only — storage is not a trusted source.
 */
// Each language keeps its own titles and links when the shopper switches language.
const KEY = `tt_recent_v3:${document.documentElement.lang || 'en'}`;
const STORE_MAX = 12;
const SHOW_MAX = 3;

function read() {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(list) ? list.filter((e) => e && e.u && e.t) : [];
  } catch {
    return [];
  }
}

function write(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, STORE_MAX)));
  } catch {
    // Private browsing: the rail just won't remember.
  }
}

class RecentlyViewed extends HTMLElement {
  connectedCallback() {
    const current = this.dataset.url || '';
    const previous = read().filter((e) => e.u !== current);

    if (previous.length > 0) {
      this.#render(previous.slice(0, SHOW_MAX));
      this.hidden = false;
    }

    if (current) {
      write([
        { u: current, t: this.dataset.title || '', i: this.dataset.image || '', p: this.dataset.price || '' },
        ...previous,
      ]);
    }
  }

  /** @param {{u: string, t: string, i: string, p: string}[]} entries */
  #render(entries) {
    const heading = document.createElement('h2');
    heading.textContent = this.dataset.heading || '';

    const head = document.createElement('div');
    head.className = 'tt-sec__h';
    head.appendChild(heading);

    const grid = document.createElement('div');
    grid.className = 'tt-grid tt-g3 tt-recent__g';

    for (const entry of entries) {
      const card = document.createElement('a');
      card.className = 'tt-card tt-card--product';
      card.setAttribute('href', entry.u);

      if (entry.i) {
        const img = document.createElement('img');
        img.setAttribute('src', entry.i);
        img.setAttribute('alt', '');
        img.setAttribute('loading', 'lazy');
        card.appendChild(img);
      }

      const bar = document.createElement('div');
      bar.className = 'tt-card__b';
      const title = document.createElement('span');
      title.className = 'tt-card__t';
      title.textContent = entry.t;
      bar.appendChild(title);
      if (entry.p) {
        const price = document.createElement('span');
        price.className = 'tt-small tt-muted';
        price.textContent = entry.p;
        bar.appendChild(price);
      }
      card.appendChild(bar);
      grid.appendChild(card);
    }

    this.replaceChildren(head, grid);
  }
}

if (!customElements.get('tt-recent')) {
  customElements.define('tt-recent', RecentlyViewed);
}
